import React, { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { useRouteMatch } from 'react-router-dom';
import { Map } from 'immutable';

import {
    fetchExtension,
    getCalendar,
    updateOpeningHours,
} from 'flow/actions/extension-actions';
import {
    clearCalendarEvents,
    fetchProfiles,
    updateCalendar,
} from 'flow/actions/profile-actions';
import CalendarBase from 'flow/components/calendar/CalendarBase';
import Loading from 'flow/components/ui-components/Loading';
import {
    selectExtensionKeyFromContactKey,
    selectExtensionActiveProfile,
} from 'flow/selectors/ExtensionSelectors';
import { CallParams } from 'flow/types/CallParams';
import { routes } from 'flow/types/routes';
import { getCalendarWithActiveProfileAddedToEvents } from 'flow/utils/calendar-utils';
import { generatePath } from 'flow/utils/router-utils';

import useBackToOrigin from '../hooks/useBackToOrigin';
import useProfiles from '../hooks/useProfiles';

type Params = CallParams & {
    // Set on the PBX queue route only
    entityKey?: string;
};

// The call record page this calendar belongs to.
const getCallRecordPath = (path: string, params: Params) =>
    path ===
    routes['pbx.queues.entityKey.number.callRecordKey.contactKey.calendar']
        ? generatePath(routes['pbx.queues.queueKey.callRecordKey'], {
              queueKey: params.entityKey,
              callRecordKey: params.callRecordKey,
          })
        : generatePath(routes['calls.callRecordKey'], {
              callRecordKey: params.callRecordKey,
          });

const CallContactCalendar = () => {
    const dispatch = useAppDispatch();
    const { path, params } = useRouteMatch<Params>();
    const { contactKey } = params;
    const handleBack = useBackToOrigin(() => getCallRecordPath(path, params));

    const extensionKey = useAppSelector((state) =>
        selectExtensionKeyFromContactKey(state, contactKey),
    );
    const { sortedActiveProfiles: profileList } = useProfiles(extensionKey);

    const activeProfile = useAppSelector(
        (state) => selectExtensionActiveProfile(state, extensionKey) || Map(),
    );

    const calendar = useAppSelector((state) =>
        state.extensions.getIn(['extensions', extensionKey, 'calendar']),
    );

    // On a reload or deep link the extensions list may not have loaded yet,
    // so the extension key arrives later.
    useEffect(() => {
        if (!extensionKey) {
            return;
        }
        dispatch(fetchExtension(extensionKey));
        dispatch(getCalendar(extensionKey));
        dispatch(fetchProfiles(extensionKey));
    }, [dispatch, extensionKey]);

    if (!extensionKey || !calendar || !profileList) {
        return <Loading />;
    }

    const updatedCalendar = getCalendarWithActiveProfileAddedToEvents(
        activeProfile,
        calendar,
    );

    return (
        <CalendarBase
            calendar={updatedCalendar}
            entityKey={extensionKey}
            profiles={profileList}
            showExternallyManagedInfo={true}
            onClickBack={handleBack}
            updateOpeningHours={(openingHours) => {
                dispatch(updateOpeningHours(extensionKey, openingHours));
            }}
            clearCalendar={() => dispatch(clearCalendarEvents(extensionKey))}
            updateCalendar={(updatedCalendar) => {
                dispatch(updateCalendar(extensionKey, updatedCalendar));
            }}
        />
    );
};

export default CallContactCalendar;
